import { AfterViewInit, Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ToastService, ToastType } from '../../shared/toast/toast.service';
import { DeveloperService } from '../services/developer.service';
import { Format } from '../models/format';
import { FormatType } from '../models/format-type';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { CurrentUserService } from '../../user/services';
import { UntypedFormGroup } from '@angular/forms';
import { DataFieldGroup } from '../../shared/datafields/models';
import { GridService } from '../../shared/grid/grid.service';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { DxDataGridModule, DxDropDownBoxModule, DxCheckBoxModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { DxoEditingModule, DxoColumnFixingModule, DxiColumnModule, DxiValidationRuleModule, DxoLookupModule, DxiButtonModule, DxoPagingModule, DxoScrollingModule, DxoSelectionModule } from 'devextreme-angular/ui/nested';
import { BizUserColumnDirective } from '../../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../../shared/grid/biz-grid-date.directive';
import { DxTemplateModule } from 'devextreme-angular/core';

import { DataFieldGroupContainerComponent } from '../../shared/datafields/datafield-group-container.component';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';

@Component({
    selector: 'app-developer-formats',
    templateUrl: './developer-formats.component.html',
    styleUrls: ['./developer-formats.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoColumnFixingModule, DxiColumnModule, DxiValidationRuleModule, DxoLookupModule, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule, DxTemplateModule, DxDropDownBoxModule, DxoPagingModule, DxoScrollingModule, DxoSelectionModule, DxCheckBoxModule, DataFieldGroupContainerComponent]
})
export class DeveloperFormatsComponent implements OnInit, AfterViewInit {
    formats: Format[] = [];
    formatTypes: FormatType[] = [];
    formatTypeDisassemblerDataFields: DataFieldGroup[] = [];
    formatTypeDisassemblerFormGroup: UntypedFormGroup = new UntypedFormGroup({});
    selectedFormatType: FormatType | null;
    overrideDisassemblerConfig = false;

    allowAdding = false;
    allowUpdating = false;
    allowDeleting = false;
    allowShowLog = false;
    dropDownOptions = { width: 500 };

    constructor(
        private developerService: DeveloperService,
        private loadingPanelService: LoadingPanelService,
        private toastService: ToastService,
        private currentUserService: CurrentUserService,
        private gridService: GridService,
        public bizGridFiltervalues: BizGridFiltervalues) { }

    ngOnInit(): void {
        this.allowAdding = this.currentUserService.hasPermission('Developer_Action_Format_Create');
        this.allowUpdating = this.currentUserService.hasPermission('Developer_Action_Format_Edit');
        this.allowDeleting = this.currentUserService.hasPermission('Developer_Action_Format_Delete');
        this.allowShowLog = this.currentUserService.hasPermission('Developer_Action_ShowLog');
    }

    async ngAfterViewInit(): Promise<void> {
        await this.refresh();
    }

    async refresh(): Promise<void> {
        try {
            this.loadingPanelService.showLoadingPanelDirect('Loading...');
            this.formats = [];

            let formats: Format[] = [];
            await Promise.all([
                this.developerService.getFormatTypes().then(result => { this.formatTypes = result.sort((a, b) => a.Name.localeCompare(b.Name)); }),
                this.developerService.getFormats().then(result => { formats = result; })
            ]);

            // Ensure that these are set last
            this.formats = formats;
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to load data...', ToastType.error);
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    onInitNewRow(e: any): void {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        e.data = { FormatsId: null, FormatName: null, FormatTypesId: null, OverridesDisassemblerConfig: false } as Format;

        // Clean data so nothing carries over to another insert/update operation.
        this.selectedFormatType = null;
        this.overrideDisassemblerConfig = false;
        this.formatTypeDisassemblerDataFields = [];
        this.formatTypeDisassemblerFormGroup = new UntypedFormGroup({});
    }

    onRowInserting = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as Format, item => this.saveFormat(item));
    };

    onRowUpdating = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow({ ...e.key, ...e.newData } as Format, item => this.saveFormat(item), e.newData);
    };

    onRowRemoving = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as Format, item => this.deleteFormat(item));
    };

    async onEditingStart(e: any) {
        const format = e.data as Format;
        if (this.formatTypes.length > 0) {
            this.selectedFormatType = this.formatTypes.filter(x => x.Id === format.FormatTypesId)[0];
            await this.setDisassembler(format);
        }

        // Lidt hacky, men for at vi kan få de dynamiske datafield-værdier med i en save, hvor man ikke har ændret andet end disse værdier, så trigger vi en ændring her, så update altid bliver kaldt.
        e.component.cellValue(e.component.getRowIndexByKey(e.key), 'FormatName', e.component.cellValue(e.component.getRowIndexByKey(e.key), 'FormatName'));
    }

    async saveFormat(format: Format): Promise<Format> {
        const dataFields = this.overrideDisassemblerConfig ? this.formatTypeDisassemblerDataFields : [];
        const formatTypeDisassemblerDataFieldValues = DataFieldGroup.getDataFieldValues(dataFields, false);
        const newFormat = await this.developerService.saveFormat(format, formatTypeDisassemblerDataFieldValues, false);
        this.toastService.showToast('Format saved...', ToastType.success);

        // Clean data so nothing carries over to another insert/update operation.
        this.selectedFormatType = null;
        this.overrideDisassemblerConfig = false;
        this.formatTypeDisassemblerDataFields = [];
        this.formatTypeDisassemblerFormGroup = new UntypedFormGroup({});

        return newFormat;
    }

    async deleteFormat(format: Format): Promise<Format> {
        await this.developerService.deleteFormat(format.FormatsId);
        this.toastService.showToast('Format deleted...', ToastType.success);
        return format;
    }

    async onRowValidating(ev: any): Promise<void> {
        if (this.overrideDisassemblerConfig) {
            this.formatTypeDisassemblerFormGroup.updateValueAndValidity();
            ev.isValid = ev.isValid && this.formatTypeDisassemblerFormGroup.valid;
        }
    }

    async setDisassembler(format: Format) {
        this.formatTypeDisassemblerDataFields = [];
        if (this.selectedFormatType?.DisassemblerInitStepId) {
            const formatTypeId = this.selectedFormatType?.Id;
            if (formatTypeId) {
                this.formatTypeDisassemblerDataFields = await this.developerService.getFormatDataFieldGroups(formatTypeId, format?.FormatsId);
                this.overrideDisassemblerConfig = format?.OverridesDisassemblerConfig ?? false;
            }
        }
    }

    onValueChanged(e: any) {
        this.overrideDisassemblerConfig = e.value;
    }

    // For use with grid in drop down template
    onSelectionChanged(selectedRowKeys, cellInfo, dropDownBoxComponent): void {
        cellInfo.setValue(selectedRowKeys[0]);
        if (selectedRowKeys.length > 0) {
            dropDownBoxComponent.close();
            this.selectedFormatType = this.formatTypes.filter(x => x.Id === selectedRowKeys[0])[0];
            this.setDisassembler(cellInfo.data.FormatsId);
        }
        else {
            this.selectedFormatType = null;
            this.overrideDisassemblerConfig = false;
            this.formatTypeDisassemblerDataFields = [];
            this.formatTypeDisassemblerFormGroup = new UntypedFormGroup({});
        }
    }
}
